import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, CheckCheck, ChevronRight, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { timeAgo } from '../../utils/timeAgo';
import useEscapeKey from '../../hooks/useEscapeKey';
import MentionRenderer from '../effeisbuck/MentionRenderer';
import {
  NOTIFICATIONS_CHANGED_EVENT,
  apriDestinazioneNotifica,
  notificaCambioNotifiche,
  risolviDestinazioneNotifica,
} from '../../lib/notificheNavigazione';

export default function NotificationBell() {
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await api.get('/notifications/unread-count');
      setUnreadCount(res.data?.count ?? 0);
    } catch (_) {
      // silent
    }
  }, []);

  const fetchRecentNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/notifications?page=1&limit=10');
      setNotifications(res.data || []);
    } catch (_) {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll for unread count every 15 seconds and react immediately to local changes
  useEffect(() => {
    fetchUnreadCount();
    const pollingInterval = setInterval(fetchUnreadCount, 15000);
    const onChanged = () => {
      fetchUnreadCount();
    };
    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, onChanged);
    return () => {
      clearInterval(pollingInterval);
      window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, onChanged);
    };
  }, [fetchUnreadCount]);

  useEscapeKey(() => setOpen(false), open);

  // Close panel when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  async function handleOpen() {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    await fetchRecentNotifications();
  }

  async function handleMarkAllRead() {
    try {
      await api.post('/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
      notificaCambioNotifiche();
    } catch (_) {
      // silent
    }
  }

  async function handleNotificationClick(n) {
    setOpen(false);
    if (!n.read) {
      setNotifications((prev) =>
        prev.map((item) => (item.id === n.id ? { ...item, read: true } : item))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
      api
        .patch(`/notifications/${n.id}/read`)
        .then(() => notificaCambioNotifiche())
        .catch(() => {});
    }
    apriDestinazioneNotifica(n, navigate);
  }

  function handleViewAll() {
    setOpen(false);
    navigate('/notifiche');
  }

  const typeColors = {
    info: 'var(--wfm-primary-light)',
    warning: 'var(--wfm-warning)',
    error: 'var(--wfm-error)',
    success: 'var(--wfm-success)',
  };

  return (
    <div className="relative" ref={panelRef}>
      <style>{`
        @keyframes notif-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.3); }
        }
        .notif-dot-pulse {
          animation: notif-pulse 1.5s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .notif-dot-pulse {
            animation: none;
          }
        }
      `}</style>
      <button
        type="button"
        onClick={handleOpen}
        aria-label="Notifiche"
        aria-expanded={open}
        className="relative p-2 text-text-mid hover:text-text-main rounded-lg hover:bg-bg-light"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span
            className="notif-dot-pulse absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-[var(--wfm-accent)]"
            style={{ boxShadow: '0 0 0 2px var(--wfm-bg-white)' }}
          />
        )}
      </button>

      {open && (
        <div className="fixed left-3 right-3 top-[calc(env(safe-area-inset-top,0px)+4.25rem)] w-auto max-w-sm mx-auto sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80 sm:max-w-none sm:mx-0 bg-bg-white rounded-xl border border-[var(--wfm-border)] shadow-xl z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--wfm-border)] bg-[var(--wfm-bg-light)]">
            <h3 className="text-sm font-semibold text-[var(--wfm-primary-ink)]">Notifiche</h3>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAllRead}
                className="flex items-center gap-1 text-xs text-[var(--wfm-primary-ink)] hover:underline font-medium"
              >
                <CheckCheck size={14} />
                Segna tutto come letto
              </button>
            )}
          </div>

          {/* Content */}
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="px-4 py-8 text-center text-sm text-[var(--wfm-text-light)]">
                Caricamento...
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <p className="text-sm font-medium text-[var(--wfm-text-main)]">
                  Nessuna notifica ricevuta
                </p>
                <p className="text-xs text-[var(--wfm-text-light)] mt-1">
                  Gli avvisi operativi e le menzioni compariranno qui.
                </p>
              </div>
            ) : (
              notifications.map((n) => {
                const dest = risolviDestinazioneNotifica(n);
                const DestIcon = dest.kind === 'external' ? ExternalLink : ChevronRight;
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => handleNotificationClick(n)}
                    className={`w-full text-left px-4 py-3 border-b border-[var(--wfm-border)] last:border-b-0 hover:bg-[var(--wfm-bg-hover)] focus-visible:bg-[var(--wfm-bg-hover)] cursor-pointer transition-colors group ${
                      !n.read ? 'bg-[var(--wfm-bg-light)]' : ''
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <div
                        className="w-2 h-2 rounded-full mt-1.5 shrink-0"
                        style={{
                          backgroundColor: n.read ? 'var(--wfm-border)' : (typeColors[n.type] || typeColors.info),
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className={`text-xs truncate text-[var(--wfm-primary-ink)] group-hover:underline ${!n.read ? 'font-bold' : 'font-medium'}`}>
                            <span className="text-[11px] font-mono text-[var(--wfm-text-light)] mr-1.5 tabular-nums">
                              {n.createdAt ? new Date(n.createdAt).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : ''}
                            </span>
                            {n.title}
                          </p>
                          <span className="text-[11px] text-[var(--wfm-text-light)] whitespace-nowrap tabular-nums">
                            {timeAgo(n.createdAt)}
                          </span>
                        </div>
                        <p className="text-xs text-[var(--wfm-text-mid)] mt-0.5 line-clamp-2">
                          <MentionRenderer body={n.body} interactive={false} />
                        </p>
                      </div>
                      <DestIcon
                        size={14}
                        aria-hidden="true"
                        className="mt-1 shrink-0 text-[var(--wfm-text-light)] group-hover:text-[var(--wfm-primary-ink)] transition-colors"
                      />
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-[var(--wfm-border)] bg-[var(--wfm-bg-light)]">
            <button
              type="button"
              onClick={handleViewAll}
              className="w-full px-4 py-2.5 text-xs font-semibold text-[var(--wfm-primary-ink)] hover:bg-[var(--wfm-bg-hover)] transition-colors text-center"
            >
              Vedi tutte le notifiche
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
